import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ChevronDown, Compass, ShieldCheck } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Progress } from "../components/Progress";
import { CompanionOverlay } from "../components/CompanionOverlay";
import {
  RodCountPicker,
  preferredRodCount,
  rememberRodCount,
} from "../components/RodCountPicker";
import { formatAdvisorValue } from "../../../../lib/carpintel-advisor";

type ConfidenceLevel =
  | "No evidence"
  | "Low Confidence"
  | "Medium Confidence"
  | "High Confidence";

function confidenceClass(level: ConfidenceLevel): string {
  if (level === "High Confidence") return "bg-success-weak text-success";
  if (level === "Medium Confidence") return "bg-info-weak text-info";
  if (level === "Low Confidence") return "bg-warning-weak text-warning";
  return "bg-inset text-secondary";
}

function scoreProgressVariant(
  score: number | null,
  level: ConfidenceLevel,
): "default" | "success" | "warning" | "error" {
  if (score === null || level === "No evidence") return "default";
  if (score >= 65) return "success";
  if (score >= 35) return "warning";
  return "default";
}

function scoreFor(advice: any): number | null {
  const level = (advice?.confidence_level ?? "No evidence") as ConfidenceLevel;
  const value = Number(advice?.fishing_score ?? 0);
  return level !== "No evidence" && Number.isFinite(value) && value > 0
    ? value
    : null;
}

function explanationsFor(advice: any): Record<string, string> {
  try {
    return typeof advice?.explanation_json === "string"
      ? JSON.parse(advice.explanation_json || "{}")
      : advice?.explanation_json || {};
  } catch {
    return {};
  }
}

function provenanceFor(advice: any): Record<string, string> {
  try {
    return typeof advice?.evidence_provenance_json === "string"
      ? JSON.parse(advice.evidence_provenance_json || "{}")
      : advice?.evidence_provenance_json || {};
  } catch {
    return {};
  }
}

function firstSentence(text?: string | null): string | undefined {
  if (!text) return undefined;
  const match = text.trim().match(/^.*?[.!?](?:\s|$)/);
  return match?.[0]?.trim() || text.trim();
}

function compactEvidenceSummary(
  source: string | undefined,
  fallback?: string | null,
): string | undefined {
  if (source === "same-lake")
    return "From successful fishing recorded on this lake.";
  if (source === "cross-water")
    return "From successful comparable fishing on other waters.";
  if (source === "external-lake")
    return "From recent public reports for this lake.";
  return firstSentence(fallback);
}

function scoutSelectionFor(advice: any): {
  fixedSwimName?: string;
  observationLabels?: string[];
  scoutConfidence?: string;
} | null {
  const explanations = explanationsFor(advice);
  try {
    if (!explanations.scoutSelection) return null;
    return JSON.parse(explanations.scoutSelection);
  } catch {
    return null;
  }
}

function anglerPatternLabel(status: string | null | undefined) {
  switch (String(status || "").toLowerCase()) {
    case "established":
      return "Strong pattern";
    case "supported":
      return "Worth trying";
    case "emerging":
      return "Early indication";
    case "changing":
      return "Pattern changing";
    case "contradicted":
      return "Evidence against";
    default:
      return "Fishing theory only";
  }
}

function Recommendation({
  label,
  value,
  qualifier,
  explanation,
  lesson,
  limit,
  patternStatus,
  whyToday,
}: {
  label: string;
  value: string | number | null | undefined;
  qualifier?: string | null;
  explanation?: string | null;
  lesson?: string | null;
  limit?: string | null;
  patternStatus?: string | null;
  whyToday?: string | null;
}) {
  const hasRecommendation =
    value !== null && value !== undefined && String(value).trim() !== "";

  return (
    <details className="group rounded-lg border border-border bg-background">
      <summary className="flex min-h-18 cursor-pointer list-none items-center justify-between gap-3 p-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-secondary">{label}</p>
          <p className={`mt-1 text-sm font-semibold ${hasRecommendation ? "text-primary" : "text-secondary"}`}>
            {hasRecommendation ? value : "Not enough evidence yet"}
          </p>
          {qualifier ? (
            <p className="mt-1 text-[11px] italic text-secondary">{qualifier}</p>
          ) : null}
          {hasRecommendation && patternStatus ? (
            <span className="mt-2 inline-flex rounded-full bg-inset px-2 py-0.5 text-[10px] font-semibold text-secondary">
              {anglerPatternLabel(patternStatus)}
            </span>
          ) : null}
        </div>
        <ChevronDown
          size={16}
          className="shrink-0 text-secondary transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>

      <div className="border-t border-border/70 px-3 pb-3 pt-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-secondary">
          Why this?
        </p>
        <p className="mt-1 text-xs leading-5 text-secondary">
          {explanation ||
            "CatchCompass does not have enough reliable evidence for this part of the plan yet."}
        </p>

        {whyToday ? (
          <div className="mt-3 rounded-md bg-inset p-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
              Why this fits today
            </p>
            <p className="mt-1 text-[11px] leading-5 text-secondary">{whyToday}</p>
          </div>
        ) : null}

        {lesson ? (
          <div className="mt-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
              What to learn
            </p>
            <p className="mt-1 text-xs leading-5 text-primary">{lesson}</p>
          </div>
        ) : null}

        {limit ? (
          <div className="mt-3 rounded-md bg-inset p-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-secondary">
              Don't overlearn it
            </p>
            <p className="mt-1 text-[11px] leading-5 text-secondary">{limit}</p>
          </div>
        ) : null}
      </div>
    </details>
  );
}

function StartingPlan({ advice }: { advice: any }) {
  const baiting = advice.recommended_bait_approach || null;
  const items = [
    ["Where", advice.recommended_area],
    ["Rig", advice.recommended_rig],
    ["Main bait", advice.recommended_main_bait],
    ["Hookbait", advice.recommended_hookbait],
    ["Baiting", baiting],
    ["Best window", advice.best_bite_period],
  ].filter(([, value]) => value !== null && value !== undefined && String(value).trim());

  if (!items.length) return null;

  return (
    <div className="mb-3 rounded-xl border border-accent/40 bg-background p-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
        Starting plan
      </p>
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
        {items.map(([label, value]) => (
          <div key={String(label)} className="min-w-0">
            <p className="text-[11px] text-secondary">{label}</p>
            <p className="mt-0.5 break-words text-xs font-semibold text-primary">
              {String(value)}
            </p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] leading-5 text-secondary">
        Tap a recommendation below for the reason and the lesson behind it.
      </p>
    </div>
  );
}

function AdvicePanel({ advice, title }: { advice: any; title: string }) {
  const explanations = explanationsFor(advice);
  const provenance = provenanceFor(advice);
  const crossWaterScore = provenance.score === "cross-water";
  const incidentalOnlyScore =
    provenance.score === "same-lake" &&
    Number(advice.personal_session_count ?? 0) +
      Number(advice.community_session_count ?? 0) ===
      0 &&
    Number(advice.evidence_catch_count ?? 0) > 0;
  const level = (advice.confidence_level ?? "No evidence") as ConfidenceLevel;
  const score = scoreFor(advice);
  const noEvidence = level === "No evidence";
  const baitApproach = advice.recommended_bait_approach || null;
  const hasRecommendationPlan = Boolean(
    advice.recommended_area ||
      advice.best_bite_period ||
      advice.recommended_rig ||
      advice.recommended_main_bait ||
      advice.recommended_hookbait ||
      advice.recommended_additive ||
      baitApproach ||
      advice.recommended_tactic ||
      advice.alternative_tactic,
  );

  return (
    <section className="space-y-4 rounded-xl border border-border bg-raised p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
            {title}
          </p>
          <p className="mt-1 text-sm text-secondary">Independent species analysis</p>
        </div>
        <span className={`rounded-full px-2 py-1 text-xs font-medium ${confidenceClass(level)}`}>
          {level}
        </span>
      </div>

      {hasRecommendationPlan ? <StartingPlan advice={advice} /> : null}

      <div>
        <p className="text-sm text-secondary">Fishing score</p>
        {score === null ? (
          <p className="mt-1 text-2xl font-semibold text-secondary">No score yet</p>
        ) : (
          <>
            <p className="mt-1 text-4xl font-semibold tracking-tight text-primary">
              {score}<span className="text-base text-secondary">/100</span>
            </p>
            <Progress
              className="mt-3"
              value={score}
              variant={scoreProgressVariant(score, level)}
              aria-label={`${title} fishing score ${score} out of 100`}
            />
          </>
        )}
        <p className="mt-3 text-sm leading-6 text-secondary">
          {score === null
            ? "Not enough evidence yet to calculate a Fishing Score."
            : "Fishing Score is a prediction score (not the catch rate)."}
        </p>
      </div>

      <div className="rounded-lg bg-inset p-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-secondary" />
          <p className="text-xs font-semibold text-primary">
            {crossWaterScore ? "Evidence from other waters" : "Evidence from this lake"}
          </p>
        </div>
        <p className="mt-2 text-xs leading-5 text-secondary">
          {incidentalOnlyScore
            ? `${advice.evidence_catch_count ?? 0} same-lake catch${Number(advice.evidence_catch_count ?? 0) === 1 ? "" : "es"} · no targeted ${advice.target_species || "species"} session yet`
            : `${advice.personal_session_count ?? 0} personal · ${advice.community_session_count ?? 0} community · ${advice.external_evidence_count ?? 0} external · ${advice.evidence_catch_count ?? 0} comparable catches`}
        </p>
        <p className="mt-2 text-xs leading-5 text-tertiary">
          {crossWaterScore
            ? "There was not enough scoring evidence from this lake, so the Fishing Score uses comparable sessions from other waters. Location and bite-time patterns are only used when they come from this lake."
            : incidentalOnlyScore
              ? "These catches were recorded on this lake during sessions targeting another species. They give the Fishing Score a reduced positive signal, but those sessions are not counted as target-species blanks or in the target-species catch rate."
              : "The Fishing Score uses targeted sessions from this lake. Successful incidental catches can also add a smaller positive signal without turning other-species sessions into blanks."}
        </p>
      </div>

      {!noEvidence || hasRecommendationPlan ? (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <Compass size={18} className="text-secondary" />
            <h2 className="text-base font-semibold text-primary">Plan details</h2>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <Recommendation label="Peg / swim" value={advice.recommended_area} qualifier={advice.area_qualifier} explanation={compactEvidenceSummary(provenance.area, explanations.area)} lesson={explanations.lessonArea} limit={explanations.limitArea} patternStatus={explanations.patternStatusArea} whyToday={explanations.whyTodayArea} />
            <Recommendation label="Best predicted bite period" value={advice.best_bite_period} qualifier={advice.bite_period_qualifier} explanation={compactEvidenceSummary(provenance.bitePeriod, explanations.bitePeriod)} lesson={explanations.lessonBitePeriod} limit={explanations.limitBitePeriod} patternStatus={explanations.patternStatusBitePeriod} whyToday={explanations.whyTodayBitePeriod} />
            <Recommendation label="Rig" value={advice.recommended_rig} qualifier={advice.rig_qualifier} explanation={compactEvidenceSummary(provenance.rig, explanations.rig)} lesson={explanations.lessonRig} limit={explanations.limitRig} patternStatus={explanations.patternStatusRig} whyToday={explanations.whyTodayRig} />
            <Recommendation label="Main bait" value={advice.recommended_main_bait} qualifier={advice.main_bait_qualifier} explanation={compactEvidenceSummary(provenance.mainBait, explanations.mainBait)} lesson={explanations.lessonMainBait} limit={explanations.limitMainBait} patternStatus={explanations.patternStatusMainBait} whyToday={explanations.whyTodayMainBait} />
            <Recommendation label="Hookbait" value={advice.recommended_hookbait} qualifier={advice.hookbait_qualifier} explanation={compactEvidenceSummary(provenance.hookbait, explanations.hookbait)} lesson={explanations.lessonHookbait} limit={explanations.limitHookbait} patternStatus={explanations.patternStatusHookbait} whyToday={explanations.whyTodayHookbait} />
            <Recommendation label="Attractor / additive" value={advice.recommended_additive} qualifier={advice.additive_qualifier} explanation={compactEvidenceSummary(provenance.additive, explanations.additive)} lesson={explanations.lessonAdditive} limit={explanations.limitAdditive} patternStatus={explanations.patternStatusAdditive} whyToday={explanations.whyTodayAdditive} />
            <Recommendation label="Feeding approach" value={baitApproach} qualifier={advice.bait_approach_qualifier} explanation={compactEvidenceSummary(provenance.baitApproach, explanations.baitAmount)} lesson={explanations.lessonBaitAmount} limit={explanations.limitBaitAmount} patternStatus={explanations.patternStatusBaitApproach} whyToday={explanations.whyTodayBaitApproach} />
            <Recommendation label="Suggested tactic" value={advice.recommended_tactic} qualifier={advice.tactic_qualifier} explanation={compactEvidenceSummary(provenance.tactic, explanations.tactic)} lesson={explanations.lessonTactic} limit={explanations.limitTactic} patternStatus={explanations.patternStatusTactic} whyToday={explanations.whyTodayTactic} />
            <Recommendation label="Second-choice evidence-backed option" value={advice.alternative_tactic} explanation={firstSentence(explanations.alternative)} lesson={explanations.lessonAlternative} limit={explanations.limitAlternative} />
          </div>

          {(advice.recommended_depth_ft != null ||
            advice.recommended_distance_yds != null ||
            advice.recommended_bait_amount != null) ? (
            <div className="mt-3 rounded-lg border border-border bg-inset p-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-secondary">
                Context signals, not targets
              </p>
              <p className="mt-1 text-[11px] leading-5 text-secondary">
                Depth, range and feed quantity help explain previous catches, but they are
                deliberately not treated as fixed recommendations.
              </p>
              {advice.recommended_depth_ft != null ? (
                <p className="mt-2 text-[11px] leading-5 text-secondary">
                  <strong className="text-primary">Depth context:</strong>{" "}
                  {formatAdvisorValue(advice.recommended_depth_ft, "ft")} ·{" "}
                  {explanations.lessonDepth || explanations.depth}
                </p>
              ) : null}
              {advice.recommended_distance_yds != null ? (
                <p className="mt-2 text-[11px] leading-5 text-secondary">
                  <strong className="text-primary">Range context:</strong>{" "}
                  {formatAdvisorValue(advice.recommended_distance_yds, "yds")} ·{" "}
                  {explanations.lessonDistance || explanations.distance}
                </p>
              ) : null}
              {advice.recommended_bait_amount != null ? (
                <p className="mt-2 text-[11px] leading-5 text-secondary">
                  <strong className="text-primary">Feed quantity:</strong>{" "}
                  {explanations.lessonBaitAmount || explanations.baitAmount}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="rounded-lg bg-inset p-3 text-sm leading-6 text-secondary">
          {hasRecommendationPlan
            ? "There is not enough evidence from this lake for a Fishing Score yet. Where useful, the plan can still use successful comparable fishing from other waters."
            : explanations.confidence ||
              "Not enough species-specific evidence is available yet to make a tactical recommendation."}
        </div>
      )}
    </section>
  );
}

function PageMessage({ text, link = "/scout" }: { text: string; link?: string }) {
  return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center">
      <p className="text-sm text-secondary">{text}</p>
      <Link to={link} className="mt-4 inline-block text-sm font-medium text-accent hover:underline">
        Back
      </Link>
    </div>
  );
}

export default function AdvisorBothResultsPage() {
  const [searchParams] = useSearchParams();
  const carpId = searchParams.get("carp");
  const catfishId = searchParams.get("catfish");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [startingSession, setStartingSession] = useState(false);
  const [startSessionError, setStartSessionError] = useState("");
  const [rodCount, setRodCount] = useState(() => preferredRodCount());

  const carpQuery = useQuery({
    queryKey: ["carpintel_advisor_recommendation", carpId],
    queryFn: () =>
      fetch(`/app-api/advisor/recommendations/${carpId}`).then((response) => {
        if (!response.ok) throw new Error("Carp advice not found");
        return response.json();
      }),
    enabled: Boolean(carpId),
  });

  const catfishQuery = useQuery({
    queryKey: ["carpintel_advisor_recommendation", catfishId],
    queryFn: () =>
      fetch(`/app-api/advisor/recommendations/${catfishId}`).then((response) => {
        if (!response.ok) throw new Error("Catfish advice not found");
        return response.json();
      }),
    enabled: Boolean(catfishId),
  });

  if (!carpId || !catfishId)
    return <PageMessage text="Both-species advice is missing one of its recommendation links." />;
  if (carpQuery.isLoading || catfishQuery.isLoading)
    return <PageMessage text="Building both fishing advice sets…" />;
  if (carpQuery.error || catfishQuery.error || !carpQuery.data || !catfishQuery.data)
    return <PageMessage text="Both-species advice could not be loaded." />;

  const carp = carpQuery.data;
  const catfish = catfishQuery.data;
  const carpScore = scoreFor(carp);
  const catfishScore = scoreFor(catfish);
  const scoutSelection = scoutSelectionFor(carp) ?? scoutSelectionFor(catfish);
  const linkedSessionId = carp.actual_session_id || catfish.actual_session_id || null;

  let lean = "Not enough evidence to compare";
  const canCompareSpecies = carpScore !== null && catfishScore !== null;
  if (canCompareSpecies) {
    lean = "No clear lean";
    const difference = carpScore - catfishScore;
    if (difference >= 3) lean = "Carp";
    if (difference <= -3) lean = "Catfish";
  }

  const leadAdvice =
    catfishScore !== null && (carpScore === null || catfishScore > carpScore)
      ? catfish
      : carp;
  const secondaryAdvice = leadAdvice.id === carp.id ? catfish : carp;
  const hasForecast = [
    leadAdvice.forecast_air_temp_c,
    leadAdvice.forecast_pressure_hpa,
    leadAdvice.forecast_pressure_trend,
    leadAdvice.forecast_wind_direction,
    leadAdvice.forecast_wind_speed_mph,
    leadAdvice.forecast_cloud_pct,
    leadAdvice.forecast_precipitation_mm,
  ].some((value) => value !== null && value !== undefined && value !== "");

  async function linkRecommendation(adviceId: number | string, sessionId: number) {
    const response = await fetch(`/app-api/advisor/recommendations/${adviceId}/link`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ actual_session_id: sessionId }),
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.error || "Could not link both advice sets to the session");
    }
  }

  async function handleStartSession(event: React.MouseEvent) {
    event.preventDefault();
    if (linkedSessionId) {
      navigate(`/sessions/${linkedSessionId}`);
      return;
    }

    setStartingSession(true);
    setStartSessionError("");
    try {
      const lakeId = Number(leadAdvice.lake_id);
      if (!lakeId || Number(carp.lake_id) !== Number(catfish.lake_id))
        throw new Error("The paired recommendations do not point to the same lake.");

      let startAt = new Date().toISOString();
      if (leadAdvice.fishing_date && leadAdvice.expected_start_time) {
        const hm = String(leadAdvice.expected_start_time).slice(0, 5);
        startAt = new Date(`${String(leadAdvice.fishing_date).slice(0, 10)}T${hm}`).toISOString();
      }

      const rawTrend = String(leadAdvice.forecast_pressure_trend ?? "").trim();
      const pressureTrend = ["rising", "falling", "stable"].includes(rawTrend)
        ? rawTrend
        : "not-recorded";
      const precipMm = leadAdvice.forecast_precipitation_mm;
      const rainObserved =
        precipMm !== null && precipMm !== undefined
          ? Number(precipMm) > 0
            ? "true"
            : "false"
          : "not-recorded";

      const sessionResponse = await fetch("/app-api/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lake_id: lakeId,
          peg: leadAdvice.recommended_area || carp.recommended_area || catfish.recommended_area || "",
          start_at: startAt,
          end_at: null,
          rods_count: rodCount,
          target_species: "both",
          weather: "",
          rain_observed: rainObserved,
          pressure_trend: pressureTrend,
          air_temp_c: leadAdvice.forecast_air_temp_c ?? null,
          wind_direction: leadAdvice.forecast_wind_direction ?? null,
          wind_speed_mph: leadAdvice.forecast_wind_speed_mph ?? null,
          air_pressure_hpa: leadAdvice.forecast_pressure_hpa ?? null,
          cloud_cover_pct: leadAdvice.forecast_cloud_pct ?? null,
          water_temp_c: null,
          gps_latitude: null,
          gps_longitude: null,
          privacy_setting: "Contribute anonymously to CarpIntel statistics",
          share_peg_anonymously: false,
          notes: "",
          advisor_recommendation_id: leadAdvice.id,
        }),
      });
      const session = await sessionResponse.json();
      if (!sessionResponse.ok || !session.id)
        throw new Error(session.error || "Could not start the mixed-target session");

      await linkRecommendation(secondaryAdvice.id, Number(session.id));
      await linkRecommendation(leadAdvice.id, Number(session.id));

      rememberRodCount(rodCount);
      await queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] });
      navigate(`/sessions/${session.id}`);
    } catch (error) {
      setStartSessionError(
        error instanceof Error ? error.message : "Could not start the mixed-target session",
      );
    } finally {
      setStartingSession(false);
    }
  }

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link to="/scout" className="text-sm font-medium text-accent hover:underline">
          Scout & Choose
        </Link>
        <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-accent">
          Both species
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-primary">
          Carp and catfish advice
        </h1>
        <p className="mt-2 text-sm leading-6 text-secondary">
          Same swim and weather, two independent Advisor runs. The scores are never averaged together.
        </p>
      </header>

      {scoutSelection ? (
        <section className="rounded-xl border border-accent/40 bg-raised p-4 sm:p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-accent">
            Scouted peg
          </p>
          <p className="mt-1 text-lg font-semibold text-primary">
            {scoutSelection.fixedSwimName || leadAdvice.recommended_area}
          </p>
          {scoutSelection.observationLabels?.length ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {scoutSelection.observationLabels.map((label) => (
                <span
                  key={label}
                  className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-secondary"
                >
                  {label}
                </span>
              ))}
            </div>
          ) : null}
          <p className="mt-3 text-xs leading-5 text-secondary">
            These are your live observations from this peg. They stay separate from the historical Fishing Scores.
          </p>
        </section>
      ) : null}

      <section className="rounded-xl border border-accent/40 bg-raised p-4 sm:p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-secondary">Current lean</p>
        <p className="mt-1 text-2xl font-bold text-primary">{lean}</p>
        {!canCompareSpecies ? (
          <p className="mt-2 text-sm leading-5 text-secondary">
            CatchCompass only shows a species lean when both carp and catfish have a valid Fishing Score.
          </p>
        ) : null}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-background p-3">
            <p className="text-xs font-semibold text-secondary">Carp</p>
            <p className="mt-1 text-xl font-bold text-primary">{carpScore === null ? "No score" : `${carpScore}/100`}</p>
            <p className="mt-1 text-xs text-secondary">{carp.confidence_level}</p>
          </div>
          <div className="rounded-lg bg-background p-3">
            <p className="text-xs font-semibold text-secondary">Catfish</p>
            <p className="mt-1 text-xl font-bold text-primary">{catfishScore === null ? "No score" : `${catfishScore}/100`}</p>
            <p className="mt-1 text-xs text-secondary">{catfish.confidence_level}</p>
          </div>
        </div>
      </section>

      <AdvicePanel advice={carp} title="Carp advice" />
      <AdvicePanel advice={catfish} title="Catfish advice" />

      <CompanionOverlay
        context={{
          type: "advisor_both",
          recommendationIds: [carp.id, catfish.id],
        }}
      />

      <section className="rounded-xl border border-border bg-raised p-4 sm:p-5">
        <h2 className="text-base font-semibold text-primary">One mixed-target session</h2>
        <p className="mt-2 text-sm leading-6 text-secondary">
          The session records that you are targeting both species. Every catch is still logged as carp or catfish, so CatchCompass keeps the learning evidence separate.
        </p>
        {!linkedSessionId ? (
          <div className="mt-4">
            <RodCountPicker
              value={rodCount}
              onChange={setRodCount}
              disabled={startingSession}
            />
          </div>
        ) : null}
        {startSessionError ? (
          <p className="mt-3 rounded-lg bg-error-weak px-3 py-2 text-sm text-error" role="alert">
            {startSessionError}
          </p>
        ) : null}
        {linkedSessionId ? (
          <Button asChild size="lg" className="mt-4 w-full sm:w-auto">
            <Link to={`/sessions/${linkedSessionId}`}>Back to live session <ArrowRight /></Link>
          </Button>
        ) : (
          <Button
            size="lg"
            className="mt-4 w-full sm:w-auto"
            isLoading={startingSession}
            onClick={handleStartSession}
          >
            Start session for both <ArrowRight />
          </Button>
        )}
      </section>

      {hasForecast ? (
        <section className="rounded-xl border border-border bg-raised p-4 sm:p-5">
          <h2 className="text-base font-semibold text-primary">Shared forecast snapshot</h2>
          <p className="mt-1 text-xs leading-5 text-secondary">
            Both species were scored against the same conditions.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div><p className="text-xs text-secondary">Air temp</p><p className="font-semibold text-primary">{leadAdvice.forecast_air_temp_c != null ? `${leadAdvice.forecast_air_temp_c}°C` : "—"}</p></div>
            <div><p className="text-xs text-secondary">Pressure</p><p className="font-semibold text-primary">{leadAdvice.forecast_pressure_hpa != null ? `${leadAdvice.forecast_pressure_hpa} hPa` : "—"}</p></div>
            <div><p className="text-xs text-secondary">Trend</p><p className="font-semibold capitalize text-primary">{leadAdvice.forecast_pressure_trend || "—"}</p></div>
            <div><p className="text-xs text-secondary">Wind</p><p className="font-semibold text-primary">{leadAdvice.forecast_wind_direction ? `${leadAdvice.forecast_wind_direction} ${leadAdvice.forecast_wind_speed_mph ?? ""} mph`.trim() : "—"}</p></div>
            <div><p className="text-xs text-secondary">Cloud</p><p className="font-semibold text-primary">{leadAdvice.forecast_cloud_pct != null ? `${leadAdvice.forecast_cloud_pct}%` : "—"}</p></div>
            <div><p className="text-xs text-secondary">Precipitation</p><p className="font-semibold text-primary">{leadAdvice.forecast_precipitation_mm != null ? `${leadAdvice.forecast_precipitation_mm} mm` : "—"}</p></div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
